import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import { BankAccount } from "@/internal/modules/providers/interfaces/BankAccount";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ref, watch } from "vue";

interface Props {
  immediate?: boolean;
  params: Params;
  config?: Config;
}

interface Config {
  errorHandling: boolean;
}

interface Params {
  id: string | number;
  page?: number;
}

interface CreateParams {
  hotelId: number;
  bankId: number;
  accountNumber: string;
  rfc: string;
  paymentType: string;
  account_id: string;
}

const getHotelBankAccount = async (
  params: Params,
  errorHandling = true,
): Promise<PaginatedResponse<BankAccount>> => {
  const { data } = await axios.get(
    `/api/internal/hotels/${params.id}/bank-accounts?page=${params.page}`,
    {
      errorHandling,
    } as CustomAxiosRequestConfig,
  );

  return data;
};

const createHotelBankAccount = async (params: CreateParams) => {
  const { data } = await axios.post(
    `/api/internal/hotels/${params.hotelId}/bank-accounts`,
    {
      bank_id: params.bankId,
      account_number: params.accountNumber,
      rfc: params.rfc,
      paymentType: params.paymentType,
      account_id: params.account_id,
    },
  );

  return data;
};

const useHotelBankAccount = ({ immediate = true, params, config }: Props) => {
  const { errorHandling = true } = config || {};

  const page = ref(1);

  const { data, isLoading, refetch } = useQuery(
    ["hotel-bank-account", params],
    () =>
      getHotelBankAccount(
        {
          id: params.id,
          page: page.value,
        },
        errorHandling,
      ),
    {
      enabled: immediate,
    },
  );

  watch(page, () => {
    refetch();
  });

  const createMutation = useMutation((params: CreateParams) =>
    createHotelBankAccount(params),
  );

  return {
    data,
    isLoading,
    refetch,
    page,

    createMutation: createMutation.mutate,
    createLoading: createMutation.isLoading,
    createAsync: createMutation.mutateAsync,
  };
};

export default useHotelBankAccount;
